import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Trophy, Search, Archive as ArchiveIcon } from "lucide-react";
import { useFetch } from "@/lib/api";
import { usePageMeta, PageHeader, EmptyState, Loader, ErrorState, Pagination, OfficialBadge, BackLink } from "@/components/common";
import { Podium } from "@/components/Podium";
import { StandingsTable } from "@/components/StandingsTable";
import { fmtDate, fmtDateTime } from "@/lib/format";

function FinalCard({ f }) {
  return (
    <Link to={`/risultati/${f.event_slug}`} data-testid={`final-card-${f.event_slug}`} className="nx-card nx-card-hover p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3"><OfficialBadge official /><span className="text-[11px] text-[#C6C6CE]/50 font-num">{fmtDate(f.published_at)}</span></div>
      <h3 className="font-display font-bold uppercase text-lg text-[#F5F5F7] leading-tight">{f.event_title}</h3>
      <div className="text-xs text-[#C6C6CE]/60">{[f.event_mode, f.event_format].filter(Boolean).join(" · ")}</div>
      <ol className="space-y-1.5 mt-auto">
        {f.rows.map((r) => (
          <li key={r.registration_id} className="flex items-center gap-3 text-sm">
            <span className={`font-num font-bold w-5 ${r.position === 1 ? "text-[#d6b3ff]" : "text-[#C6C6CE]"}`}>{r.position}</span>
            <span className="text-[#F5F5F7] truncate flex-1">{r.name}</span>
            <span className="font-num text-xs text-[#C6C6CE]/60">{r.points} pt</span>
          </li>
        ))}
      </ol>
    </Link>
  );
}

export function FinalsList({ archive = false }) {
  usePageMeta(archive ? "Archivio storico" : "Risultati finali", "Risultati ufficiali e archivio delle edizioni NEXUM LEGACY.");
  const [f, setF] = useState({ page: 1 });
  const clans = useFetch(archive ? "/clans" : null, { limit: 100 });
  const meta = useFetch(archive ? "/events-meta" : null);
  const { data, loading, error, reload } = useFetch("/finals", f);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v || undefined, page: 1 }));
  return (
    <div data-testid={archive ? "archive-page" : "results-page"}>
      <PageHeader eyebrow={archive ? "Storia competitiva" : "Ufficiale"} title={archive ? "Archivio storico" : "Risultati finali"} subtitle={archive ? "Consulta le edizioni precedenti per anno, evento, clan e tipologia." : "Classifiche definitive verificate e pubblicate dallo staff."} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        {archive && (
          <div className="flex flex-wrap gap-2.5 mb-8">
            <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#C6C6CE]/40" /><input data-testid="archive-search" placeholder="Cerca evento" className="nx-input pl-9" onChange={(e) => set("q", e.target.value)} /></div>
            <select data-testid="archive-filter-year" aria-label="Anno" className="nx-input w-auto" onChange={(e) => set("year", e.target.value)}><option value="">Tutti gli anni</option>{(data?.years || []).map((y) => <option key={y}>{y}</option>)}</select>
            <select data-testid="archive-filter-clan" aria-label="Clan" className="nx-input w-auto" onChange={(e) => set("clan_id", e.target.value)}><option value="">Tutti i clan</option>{(clans.data?.items || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
            <select data-testid="archive-filter-mode" aria-label="Modalità" className="nx-input w-auto" onChange={(e) => set("mode", e.target.value)}><option value="">Tutte le modalità</option>{(meta.data?.modes || []).map((m) => <option key={m}>{m}</option>)}</select>
            <select data-testid="archive-filter-format" aria-label="Formato" className="nx-input w-auto" onChange={(e) => set("format", e.target.value)}><option value="">Tutti i formati</option>{(meta.data?.formats || []).map((m) => <option key={m}>{m}</option>)}</select>
          </div>
        )}
        {error ? <ErrorState message={error} onRetry={reload} /> : loading && !data ? <Loader /> : data.items.length ? (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.items.map((x) => <FinalCard key={x.id} f={x} />)}</div>
            <Pagination page={data.page} pages={data.pages} onChange={(p) => setF((x) => ({ ...x, page: p }))} />
          </>
        ) : <EmptyState icon={archive ? ArchiveIcon : Trophy} title="Nessun risultato ufficiale" text="Le classifiche definitive compariranno qui dopo la pubblicazione da parte dello staff." />}
      </div>
    </div>
  );
}

export function ResultDetail() {
  const { slug } = useParams();
  const { data, loading, error, reload } = useFetch(`/finals/${slug}`);
  usePageMeta(data ? `Risultati ${data.event.title}` : "Risultati");
  if (loading && !data) return <div className="pt-24"><Loader /></div>;
  if (error) return <div className="max-w-3xl mx-auto px-5 pt-32"><ErrorState message={error} onRetry={reload} /></div>;
  const { event: ev, final, versions } = data;
  return (
    <div data-testid="result-detail-page">
      <PageHeader eyebrow="Classifica definitiva" title={ev.title}>
        <div className="flex flex-wrap items-center gap-3"><OfficialBadge official /><span className="text-xs text-[#C6C6CE]/60">Versione {final.version} · pubblicata il {fmtDateTime(final.published_at)} da {final.published_by_name}</span></div>
        <div className="mt-4"><BackLink to="/risultati" label="Tutti i risultati" /></div>
      </PageHeader>
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 space-y-12">
        <div className="nx-card p-6 md:p-12 relative overflow-hidden"><div className="absolute inset-x-0 -top-32 h-64 purple-glow" /><div className="relative"><Podium rows={final.rows} /></div></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="final-stats">
          {[["Partecipanti", final.stats?.participants], ["Risultati registrati", final.stats?.results], ["Eliminazioni totali", final.stats?.total_kills], ["Punti totali", final.stats?.total_points]].map(([l, v]) => (
            <div key={l} className="nx-card p-5"><div className="text-[10px] uppercase tracking-widest text-[#C6C6CE]/50">{l}</div><div className="font-num text-2xl font-bold text-[#F5F5F7] mt-1">{v ?? "—"}</div></div>
          ))}
        </div>
        <StandingsTable rows={final.rows} extraStats={final.score_rules?.extra_stats || []} showChange={false} showPrize />
        <div className="grid md:grid-cols-2 gap-6">
          <div className="nx-card p-6"><div className="eyebrow mb-3">Regolamento applicato</div><p className="prose-nx text-sm">{final.regulation_snapshot ? `${final.regulation_snapshot.title} (v${final.regulation_snapshot.version})\n\n` : ""}{final.rules_snapshot || "—"}</p></div>
          <div className="nx-card p-6" data-testid="final-versions"><div className="eyebrow mb-3">Storico versioni</div>
            <ul className="space-y-3 text-sm">
              {versions.map((v) => (
                <li key={v.id} className="border-b border-[#C6C6CE]/5 pb-2">
                  <div className="flex justify-between"><span className="text-[#F5F5F7]">Versione {v.version} {v.status === "official" ? "· ufficiale" : "· sostituita"}</span><span className="font-num text-xs text-[#C6C6CE]/50">{fmtDateTime(v.published_at)}</span></div>
                  {v.correction_reason && <div className="text-xs text-amber-200 mt-1">Correzione: {v.correction_reason}</div>}
                </li>
              ))}
            </ul>
            {final.notes && <p className="mt-4 text-xs text-[#C6C6CE]/60">Note: {final.notes}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
